import { useMemo, useState } from 'react';
import Fuse from 'fuse.js';
import { useRouter } from 'next/navigation';

import { IZoneWithDestinationsAndPromo } from '@/src/modules/destinations/destinations.modules';
import { extendedCountriesByZone } from '@/src/utils/extendedCountries';

const makeExtendedCountries = (zone: string) => {
  const extendedCountries = extendedCountriesByZone[zone] || [];
  return extendedCountries.map((country: string) => ({
    label: country,
    code: '',
    flagName: '',
  }));
};

function fuzzySearch(
  data: Record<string, IZoneWithDestinationsAndPromo>,
  searchTerm: string
) {
  if (!searchTerm) return [];

  return Object.entries(data)
    .map(([zoneName, item]) => {
      const fuseCountries = new Fuse(
        [...item.countries, ...makeExtendedCountries(zoneName)],
        {
          keys: ['label'],
          threshold: 0.2,
          shouldSort: true,
        }
      );

      const filteredCountries = fuseCountries
        .search(searchTerm)
        .map(result => result.item);

      const fuseName = new Fuse([zoneName], {
        threshold: 0.2,
        shouldSort: true,
      });

      const nameSearchResults = fuseName.search(searchTerm);

      const isExactMatch = zoneName
        .toLowerCase()
        .startsWith(searchTerm.toLowerCase());
      const isNameMatch = nameSearchResults.length > 0;

      const score = isExactMatch
        ? -1
        : (isNameMatch ? nameSearchResults[0].score || 0 : 1) +
          (filteredCountries.length > 0 ? 0.5 : 1);

      return {
        ...item,
        isMatch: isExactMatch || isNameMatch || filteredCountries.length > 0,
        score,
      };
    })
    .filter(item => item.isMatch)
    .sort((a, b) => a.score - b.score);
}

export const useInputSearch = (
  data?: Record<string, IZoneWithDestinationsAndPromo>
) => {
  const [search, setSearch] = useState('');
  const [focus, setFocus] = useState(false);
  const router = useRouter();

  const handleFocus = () => {
    setFocus(true);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setFocus(false);
    }, 200);
  };

  const destinationsMemoized = useMemo(() => {
    if (data) {
      if (search) {
        return fuzzySearch(data, search);
      }
      return [];
    }
    return [];
  }, [data, search]);

  const handleRedirect = (url: string) => {
    window.dataLayer?.push({
      event: 'search',
      search_term: search,
    });
    router.push(url);
  };

  return {
    search,
    setSearch,
    focus,
    destinationsMemoized,
    handleFocus,
    handleBlur,
    handleRedirect,
  };
};
